// Which medical questions an order actually needs.
//
// An order for an add-on on its own (NAD, Naltrexone, Sermorelin) is screened
// with that add-on's questions ONLY — the GLP-1 weight-loss intake does not
// apply to it. An order that includes a GLP-1 keeps the weight-loss intake and
// gets the add-on questions appended after it.
//
// Questionnaire content comes only from catalog questionnaires_url values.
// The metadata below preserves the answer identifiers expected by submission.

import { findInCatalog } from './catalogIndex.js';

// Alternate spellings a bundle uses for a component that is really one of the
// codes above. The nasal Longevity plans list NAD_NASAL; it is the same
// medication as NAD and is screened with the same three questions.
const COMPONENT_ALIASES = {
    NAD_NASAL: 'NAD',
    NASAL_NAD: 'NAD',
    LDN: 'NALTREXONE'
};

// Component codes, spelled the way products spell them in `bundle_components`.
export const COMPONENT = {
    TIRZ: 'TIRZ',
    SEMA: 'SEMA',
    NAD: 'NAD',
    NALTREXONE: 'NALTREXONE',
    SERMORELIN: 'SERMORELIN',
    DHEA: 'DHEA',
    METFORMIN: 'METFORMIN',
    ONDANSETRON: 'ONDANSETRON',
    GLP_BALANCE: 'GLP_BALANCE'
};

// Components that make an order a GLP-1 order.
const GLP_COMPONENTS = [COMPONENT.TIRZ, COMPONENT.SEMA];

// Medication keywords are maintained by the backend per product.
const KEYWORD_TO_COMPONENT = [
    { match: 'tirzepatide', component: COMPONENT.TIRZ },
    { match: 'semaglutide', component: COMPONENT.SEMA },
    { match: 'nad', component: COMPONENT.NAD },
    { match: 'naltrexone', component: COMPONENT.NALTREXONE },
    { match: 'ldn', component: COMPONENT.NALTREXONE },
    { match: 'sermorelin', component: COMPONENT.SERMORELIN },
    { match: 'dhea', component: COMPONENT.DHEA },
    { match: 'metformin', component: COMPONENT.METFORMIN },
    { match: 'ondansetron', component: COMPONENT.ONDANSETRON }
];

const QUESTION_SETS = {
    [COMPONENT.NAD]: { prefix: 'nad_', answerIds: [] },
    [COMPONENT.NALTREXONE]: {
        prefix: 'nal_',
        answerIds: ['naltrexone_currently_taking', 'naltrexone_dose_preference']
    },
    [COMPONENT.SERMORELIN]: {
        prefix: 'sermo_',
        answerIds: ['sermorelin_currently_taking', 'sermorelin_dose_preference']
    }
};

// Preserve established submission ids; other catalog component codes namespace
// their answer keys mechanically. This metadata never decides whether to ask.
const questionIdentifiers = (code) => {
    if (QUESTION_SETS[code]) return QUESTION_SETS[code];
    return { prefix: `${code.toLowerCase()}_`, answerIds: [] };
};

const readProductId = (record) => {
    if (!record) return null;
    const raw = record.productObjectId || record.product_id;
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    return raw.$oid || null;
};

const componentForKeywords = (keywords) => {
    if (!Array.isArray(keywords)) return null;
    const lowered = keywords.map((keyword) => String(keyword).toLowerCase());
    const hit = KEYWORD_TO_COMPONENT.find(({ match }) =>
        lowered.some((keyword) => keyword.includes(match))
    );
    return hit ? hit.component : null;
};

const unique = (values) => [...new Set(values.filter(Boolean))];

const normalizeComponent = (code) => {
    const upper = String(code).toUpperCase();
    return COMPONENT_ALIASES[upper] || upper;
};

/**
 * The medications an order covers, as component codes.
 * A bundle lists its own; a single product resolves to one.
 * Returns [] for a product this module does not recognise.
 */
export const resolveOrderComponents = (product) => {
    if (!product) return [];
    if (product.is_bundle && Array.isArray(product.bundle_components)) {
        return unique(product.bundle_components.map(normalizeComponent));
    }
    if (product.internal_id) {
        const code = normalizeComponent(product.internal_id);
        return [code];
    }
    const byKeyword = componentForKeywords(product.med_keywords);
    return byKeyword ? [byKeyword] : [];
};

/** True when the order includes a GLP-1 and therefore needs the weight-loss intake. */
export const isGlpOrder = (components) =>
    (components || []).some((code) => GLP_COMPONENTS.includes(code));

/**
 * The GLP-1 an order's components prescribe: 'tirzepatide', 'semaglutide', or
 * null when they hold neither. Read from components, not the product name,
 * because the Total Reset bundles are named "Reset Protocol" and "Total Reset
 * Plan", which name neither drug.
 */
export const glpDrugFor = (components) => {
    if ((components || []).includes(COMPONENT.TIRZ)) return 'tirzepatide';
    if ((components || []).includes(COMPONENT.SEMA)) return 'semaglutide';
    return null;
};

/**
 * Whether an order gets the GLP-1 refill form instead of its own questions.
 *
 * `GET /forms/orders/:id` reports `isTriz` / `isSema` for the patient's current
 * GLP-1, not for the order: a patient on Semaglutide comes back `isSema: true`
 * on their NAD+ order as well. The refill form only asks about weight loss and
 * the next GLP-1 dose, so an order known to hold no GLP-1 never gets it.
 *
 * Takes the order's product and resolves its components here, so every caller
 * reads the order the same way. A missing product, or one that resolves to no
 * component, keeps the rule it had before: no question set exists for it, so
 * there is no better form to send it to.
 */
export const isRefillOrder = (orderDetails, product) => {
    if (!orderDetails || !orderDetails.health_id) return false;
    if (!orderDetails.isTriz && !orderDetails.isSema) return false;
    const components = resolveOrderComponents(product);
    return components.length === 0 || isGlpOrder(components);
};

/** Non-GLP components; the product URL decides whether they have questions. */
export const withQuestionSets = (components) =>
    unique(components || []).filter((code) => !GLP_COMPONENTS.includes(code));

// The catalog nests the other doses of a product inside the first one, so a
// held dose has no top-level row to match. findInCatalog looks in both places.
const productById = (products, productId) => findInCatalog(products, productId);

/**
 * Products held on active subscriptions or one-time purchases.
 *
 * An empty catalog is a load failure rather than a customer holding nothing:
 * every record would be skipped and the form would quietly drop questions the
 * patient has to answer, so that case throws and the caller can retry.
 */
export const activeAddonProducts = (subscriptions, transactions, products) => {
    if (!products || !products.length) throw new Error('Product not found');
    const records = [
        ...(subscriptions || []).filter((sub) => sub.status === 'active'),
        ...(transactions || [])
    ];
    const held = new Set();
    for (const record of records) {
        const product = productById(products, readProductId(record));
        // Transactions are billing rows — refunds and renewals name no product —
        // and a retired product has left the catalog. Such a record carries no
        // questionnaire, so it contributes nothing and must not fail the form.
        if (!product) continue;
        // A blank URL explicitly means this product contributes no questions.
        if (productQuestionnaireUrl(product) &&
            withQuestionSets(resolveOrderComponents(product)).length) {
            held.add(product);
        }
    }
    return [...held];
};

export const activeAddonComponents = (subscriptions, transactions, products) =>
    withQuestionSets(activeAddonProducts(subscriptions, transactions, products)
        .flatMap(resolveOrderComponents));

/**
 * The questionnaire a product points at, or null when the product has none.
 *
 * `questionnaires_url` is seeded per product in the catalog. A product that
 * carries one owns its whole form: the Longevity plans point at a file that
 * already merges the weight-loss intake with the NAD and Sermorelin questions.
 */
export const productQuestionnaireUrl = (product) => {
    const url = product && product.questionnaires_url;
    if (url == null || url === '') return null;
    if (typeof url !== 'string') throw new Error('Failed to load questions');
    return url.trim() || null;
};

/**
 * Marks a failure that a retry can never clear: the questionnaire the catalog
 * names is not there, or what is there is not a questionnaire. Both mean the
 * product record was never finished, which only somebody editing the catalog can
 * put right. A network failure carries no reason, because trying again fixes it.
 */
export const QUESTIONNAIRE_UNAVAILABLE = 'questionnaire-unavailable';

const unavailable = (message) =>
    Object.assign(new Error(message), { reason: QUESTIONNAIRE_UNAVAILABLE });

/**
 * Download one questionnaire. Returns { questions, dependencies }.
 * Throws with the url in the message, so a failure names the file that broke.
 */
export const fetchQuestionnaire = async (url) => {
    const response = await fetch(url, { cache: 'no-store' });
    if (!response.ok) {
        throw unavailable(`Failed to load questionnaire ${url}`);
    }
    const data = await response.json();
    if (!Array.isArray(data.questions)) {
        throw unavailable('Failed to load questions');
    }
    // An explicit empty list has no questions or dependencies to apply.
    if (!data.questions.length) return { questions: [], dependencies: [] };
    if (!Array.isArray(data.dependency)) {
        throw unavailable('Failed to load questions');
    }
    return { questions: data.questions, dependencies: data.dependency };
};

// The id and name each question has in its own file, for questions that
// namespaceProductQuestionnaire renamed. Kept off the question itself because
// the form saves its questions to localStorage and compares them on reload.
const fileNames = new WeakMap();

// Questionnaire files share a question by giving it the same id in every file:
// pregnancy is "0" in each medication's file. The name is compared as well
// because NADQuiz_9sep uses id "0" for its cancer and autoimmune screen.
// A question matches on its id and name in its own file or in the form:
// NADQuiz_9sep numbers its questions "1" and "2", the form renames them
// "nad_1" and "nad_2", and that is how MethyleneBlueQuiz numbers the same two.
const sharedKeys = (question) => {
    const inForm = `${question.id}|${question.question}`;
    const inFile = fileNames.get(question);
    return inFile ? [inFile, inForm] : [inForm];
};

/**
 * Whether `question` is the one every file asks as `id` / `name`, under any
 * prefix this form gave it: pregnancy is "0" / "pregnency" on a weight-loss
 * order and "dhea_0" / "dhea_pregnency" on a DHEA order. Id and name are read
 * together because LDN's "nal_drug_allergies" ends like "allergies" and
 * NADQuiz_9sep's "0" is its cancer screen.
 */
export const isSharedQuestion = (question, id, name) => {
    const formId = String(question.id);
    if (!formId.endsWith(id)) return false;
    const prefix = formId.slice(0, formId.length - id.length);
    return question.question === `${prefix}${name}`;
};

// Words a patient reads, without markup, case or punctuation.
const plain = (text) =>
    String(text == null ? '' : text)
        .replace(/<[^>]*>/g, ' ')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();

const TYPED_ANSWERS = ['text', 'textarea', 'string'];

// Some files ask a shared question under their own id: GLP1Quiz_9sep asks
// "Do you understand all the questions which were asked?" as 32,
// NaltrexoneQuiz_10sep as nal_19. This is what the patient sees, and when: the
// wording, the choices, and the answer that opens it. A typed answer's
// description is only a hint; a choice question's can hold the list it asks
// about, as NAD's heart screen does.
const sameScreen = (question, conditions) => {
    const typed = TYPED_ANSWERS.includes(question.question_type);
    return JSON.stringify([
        plain(question.label),
        typed ? 'typed' : question.question_type,
        typed ? '' : plain(question.description),
        (question.options || []).map((option) => plain(option.title)),
        conditions
            .map((condition) => [condition.dependent_on, (condition.ask_if_val_in || []).map(plain).sort()])
            .sort()
    ]);
};

// The answers a question stops on or flags for the doctor.
const flags = (question) =>
    [
        question.is_critical && 'question:critical',
        question.important && 'question:important',
        ...(question.options || []).flatMap((option) => [
            option.is_critical && `${plain(option.title)}:critical`,
            option.important && `${plain(option.title)}:important`
        ])
    ].filter(Boolean);

const stricter = (copy, asked) => {
    const askedFlags = new Set(flags(asked));
    return flags(copy).some((flag) => !askedFlags.has(flag));
};

/**
 * Append `extra` to `base`, skipping any question the base form already asks.
 *
 * The same medication reaches a form by two routes — inside the order's bundle
 * and again on its own subscription — and every medication file repeats the
 * shared questions (pregnancy, medications, allergies). The patient must answer
 * each question once. Dependencies for a skipped question go with it, and a
 * kept question that depended on a skipped one now depends on the copy asked.
 *
 * A question that matches on screen only, not on id, is still asked when its
 * copy stops or flags an answer the asked question does not.
 */
export const mergeQuestionnaire = (base, extra) => {
    const baseQuestions = base.questions || [];
    const baseDependencies = base.dependencies || [];
    const extraDependencies = extra.dependencies || [];
    const conditionsOf = (question, dependencies) =>
        dependencies.filter((dependency) => dependency.question_id === question.id);
    const ids = new Set();
    const askedAs = new Map();
    const screens = new Map();
    const ask = (question, conditions) => {
        ids.add(question.id);
        sharedKeys(question).forEach((key) => {
            if (!askedAs.has(key)) askedAs.set(key, question);
        });
        const screen = sameScreen(question, conditions);
        if (plain(question.label) && !screens.has(screen)) screens.set(screen, question);
    };
    baseQuestions.forEach((question) => ask(question, conditionsOf(question, baseDependencies)));
    const answeredBy = new Map();
    // Point a dependency on a skipped question at the one asked, in its spelling
    // of the answer: nal_19 is answered "No", 32 "NO".
    const follow = (dependency) => {
        const asked = answeredBy.get(dependency.dependent_on);
        if (!asked) return dependency;
        const followed = { ...dependency, dependent_on: asked.id };
        if (!Array.isArray(dependency.ask_if_val_in)) return followed;
        const spelled = (value) => {
            const option = (asked.options || []).find((choice) => plain(choice.title) === plain(value));
            return option ? option.title : value;
        };
        return { ...followed, ask_if_val_in: dependency.ask_if_val_in.map(spelled) };
    };
    const added = [];
    for (const question of extra.questions || []) {
        const conditions = conditionsOf(question, extraDependencies).map(follow);
        const shared = sharedKeys(question).find((key) => askedAs.has(key));
        const asked = shared ? askedAs.get(shared) : screens.get(sameScreen(question, conditions));
        if (asked && (shared || !stricter(question, asked))) {
            answeredBy.set(question.id, asked);
        } else if (!ids.has(question.id)) {
            ask(question, conditions);
            added.push(question);
        }
    }
    const addedIds = new Set(added.map((question) => question.id));

    return {
        questions: [...baseQuestions, ...added],
        dependencies: [
            ...baseDependencies,
            ...extraDependencies
                .filter((dependency) => addedIds.has(dependency.question_id))
                .map(follow)
        ]
    };
};

/**
 * Load add-ons from the actual held products passed by the caller. Blank paths
 * contribute nothing. Bundle files contribute only the requested add-on's
 * existing namespaced questions, so their GLP intake is not appended again.
 */
export const loadAddonQuestions = async (components, products) => {
    let assembled = { questions: [], dependencies: [] };
    const requested = new Set(withQuestionSets(components));
    const downloaded = new Map();
    for (const product of products || []) {
        const covered = resolveOrderComponents(product);
        const needed = withQuestionSets(covered).filter((code) => requested.has(code));
        const url = productQuestionnaireUrl(product);
        if (!url || !needed.length) continue;
        if (!downloaded.has(url)) downloaded.set(url, await fetchQuestionnaire(url));
        const quiz = namespaceProductQuestionnaire(downloaded.get(url), covered);
        const wholeAddon = !isGlpOrder(covered) &&
            covered.every((code) => requested.has(code));
        const questions = wholeAddon
            ? quiz.questions
            : quiz.questions.filter((question) => needed.some((code) => {
                const { prefix, answerIds } = questionIdentifiers(code);
                return String(question.id).startsWith(prefix) || answerIds.includes(question.id);
            }));
        const ids = new Set(questions.map((question) => question.id));
        assembled = mergeQuestionnaire(assembled, {
            questions,
            dependencies: quiz.dependencies.filter((dependency) => ids.has(dependency.question_id))
        });
    }
    return assembled;
};

/** Append catalog-backed add-on questions, preserving unique answer ids. */
export const withHeldAddons = async (base, components, products) => {
    const addon = await loadAddonQuestions(components, products);
    return mergeQuestionnaire(base, addon);
};

/**
 * Drop the pregnancy question for a customer who is not female.
 *
 * Matched on the question's name, not its id. The weight-loss intake calls it
 * id "0", but so does NADQuiz — where id "0" is the cancer and autoimmune
 * screen. Filtering by id would hide that screen from every male NAD patient.
 */
export const filterForGender = (questionnaire, gender) => {
    if (gender === 'Female') return questionnaire;
    return {
        ...questionnaire,
        questions: (questionnaire.questions || []).filter(
            (question) => question.question !== 'pregnency'
        )
    };
};

/**
 * Give an add-on product's own questionnaire the ids its question set uses.
 *
 * The form leaves answer "0" (pregnancy) out of what it sends, and NADQuiz_9sep
 * numbers its cancer and autoimmune screen "0". Prefixing the ids the way
 * loadAddonQuestions does keeps an add-on's answers clear of the weight-loss ids.
 *
 * A weight-loss order's file owns those ids, and an order with more than one
 * question set cannot say which prefix a question takes, so both are returned
 * as they are. Ids already prefixed, and the dose ids read by name, are kept.
 */
export const namespaceProductQuestionnaire = (questionnaire, components) => {
    const sets = withQuestionSets(components);
    if (isGlpOrder(components) || sets.length !== 1) return questionnaire;

    const { prefix, answerIds } = questionIdentifiers(sets[0]);
    const readByName = new Set(answerIds);
    const questions = questionnaire.questions || [];
    const renamed = new Set(
        questions
            .map((question) => String(question.id))
            .filter((id) => !id.startsWith(prefix) && !readByName.has(id))
    );
    const rename = (id) => (renamed.has(String(id)) ? `${prefix}${id}` : id);

    return {
        ...questionnaire,
        questions: questions.map((question) => {
            if (!renamed.has(String(question.id))) return question;
            const copy = {
                ...question,
                id: `${prefix}${question.id}`,
                question: `${prefix}${question.question}`
            };
            fileNames.set(copy, sharedKeys(question)[0]);
            return copy;
        }),
        dependencies: (questionnaire.dependencies || []).map((dependency) => ({
            ...dependency,
            question_id: rename(dependency.question_id),
            dependent_on: rename(dependency.dependent_on)
        }))
    };
};
